// derived from https://github.com/paldepind/sync-promise

const enum State {
	PENDING = 2,
	FULFILLED = 0,
	REJECTED = 1,
}

function isPromiseLike(value: any): value is PromiseLike<any> {
	return value && typeof value.then === 'function';
}

export default class ImmediatePromise<T> implements PromiseLike<T> {

	// keep value and reason for better debugging
	private value: T | undefined = undefined;
	private reason: any = undefined;

	private isConstructed = false;
	private state = State.PENDING;
	private callbacks: [
		((value: T) => void)[],
		((reason: any) => void)[]
	] | null = [[], []];
	private promise: Promise<T>;
	private ordinaryPromiseExposed = false;
	private resPromise!: (value: T) => void;
	private rejPromise!: (reason?: any) => void;

	constructor(executor: (
		resolve: (value: T | PromiseLike<T>) => void,
		reject: (reason?: any) => void
	) => void) {
		this.promise = new Promise<T>((res, rej) => {
			this.resPromise = res;
			this.rejPromise = rej;
		});
		const resolve = (value: T | PromiseLike<T>) => {
			if (this.callbacks == null) {
				return;
			}
			if (isPromiseLike(value)) {
				value.then(resolve, reject);
			} else {
				this.settle(value, State.FULFILLED);
			}
		};
		const reject = (reason?: any) => {
			if (this.callbacks == null) {
				return;
			}
			this.settle(reason, State.REJECTED);
		};
		executor(resolve, reject);
		this.isConstructed = true;
	}

	private settle(...[value, state]: [T, State.FULFILLED] | [any, State.REJECTED]) {
		if(!this.isConstructed) {
			throw new Error('Cannot settle `ImmediatePromise` while initializing');
		}
		this.state = state;
		if(state === State.FULFILLED) {
			this.value = value;
			this.resPromise(value);
		} else {
			if(this.callbacks![State.REJECTED].length > 0 && !this.ordinaryPromiseExposed) {
				this.promise.catch(e => 1);
			}
			this.reason = value;
			this.rejPromise(value);
		}
		this.callbacks![state].forEach((fn) => fn(value));
		this.callbacks = null;
	}

	then<TResult1 = T, TResult2 = never>(
		onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | undefined | null,
		onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
	): ImmediatePromise<TResult1 | TResult2> {
		if(this.state !== State.PENDING) {
			throw new Error('Cannot attach onto an resolved `ImmediatePromise`.');
		}
		return new ImmediatePromise((res, rej) => {

			this.callbacks![State.FULFILLED].push(value => {
				try {
					res(onfulfilled ? onfulfilled(value) : value as any as TResult1);
				} catch (e) {
					rej(e);
				}
			});

			if(onrejected) {
				this.callbacks![State.REJECTED].push(reason => {
					try {
						res(onrejected(reason));
					} catch (e) {
						rej(e);
					}
				});
			} else {
				this.callbacks![State.REJECTED].push(rej);
			}
			
		});
	}

	catch<TResult = never>(
		onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null
	): ImmediatePromise<T | TResult> {
		return this.then(null, onrejected);
	}

	finally(onfinally?: (() => void) | undefined | null): ImmediatePromise<T> {
		return this.then(
			(value) => {
				onfinally?.();
				return value;
			}, (reason) => {
				onfinally?.();
				throw reason;
			}
		);
	}

	getOrdinaryPromise() {
		this.ordinaryPromiseExposed = true;
		return this.promise;
	}

	[Symbol.toStringTag] = 'ImmediatePromise';

	static race<T extends readonly unknown[] | []>(values: T): ImmediatePromise<Awaited<T[number]>> {
		return new ImmediatePromise((res, rej) => {
			(values as (PromiseLike<Awaited<T[number]>> | Awaited<T[number]>)[])
				.forEach((p) => {
					if (!(p instanceof ImmediatePromise)) {
						p = Promise.resolve(p);
					}
					p.then(res, rej);
				});
		});
	}

}